import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { backupsApi, type BackupJob, type BackupStatus } from "../../api/backups.api";

const STATUS_CLASS: Record<BackupStatus, string> = {
  PENDING: "muted",
  RUNNING: "warning",
  SUCCESS: "success",
  FAILED: "danger",
  PARTIAL: "warning",
};

function StatusBadge({ status }: { status: BackupStatus }) {
  return <span className={`badge ${STATUS_CLASS[status]}`}>{status}</span>;
}

function formatBytes(bytes: string | null): string {
  if (!bytes) return "—";
  const n = Number(bytes);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDuration(start: string, end: string | null): string {
  if (!end) return "—";
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  return `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`;
}

function JobRow({ job }: { job: BackupJob }) {
  const [expanded, setExpanded] = useState(false);
  const isActive = job.status === "PENDING" || job.status === "RUNNING";

  return (
    <>
      <tr
        style={{ cursor: "pointer" }}
        onClick={() => setExpanded((v) => !v)}
      >
        <td><StatusBadge status={job.status} /></td>
        <td className="muted">{job.triggeredBy === "cron" ? "Scheduled" : "Manual"}</td>
        <td className="muted">{new Date(job.startedAt).toLocaleString()}</td>
        <td className="muted">{formatDuration(job.startedAt, job.completedAt)}</td>
        <td className="muted">{job.entries.length} DB{job.entries.length !== 1 ? "s" : ""}</td>
        <td style={{ textAlign: "right" }}>{expanded ? "▲" : "▼"}</td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={6} style={{ padding: 0 }}>
            <div style={{ padding: "8px 16px", background: "var(--bg-alt, #f8f9fa)" }}>
              {job.error && (
                <p className="error-text" style={{ marginBottom: 8 }}>{job.error}</p>
              )}
              {job.entries.length === 0 && !isActive && (
                <p className="muted" style={{ fontSize: 13 }}>No database entries.</p>
              )}
              {job.entries.length > 0 && (
                <table style={{ fontSize: 13, width: "100%" }}>
                  <thead>
                    <tr>
                      <th>Project</th>
                      <th>Service</th>
                      <th>Status</th>
                      <th>Size</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {job.entries.map((e) => (
                      <tr key={e.id}>
                        <td>{e.railwayProjectName}</td>
                        <td>{e.railwayServiceName}</td>
                        <td><StatusBadge status={e.status} /></td>
                        <td className="muted">{formatBytes(e.sizeBytes)}</td>
                        <td className="muted">{e.error ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export function AdminBackups() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ["backups", page],
    queryFn: () => backupsApi.list(page, 20),
    refetchInterval: (query) => {
      const jobs = query.state.data?.jobs ?? [];
      const hasActive = jobs.some(
        (j) => j.status === "PENDING" || j.status === "RUNNING",
      );
      return hasActive ? 3000 : false;
    },
  });

  const trigger = useMutation({
    mutationFn: backupsApi.trigger,
    onSuccess: () => {
      setPage(1);
      queryClient.invalidateQueries({ queryKey: ["backups"] });
    },
  });

  const totalPages = data ? Math.ceil(data.total / data.limit) : 1;

  return (
    <>
      <div className="page-header">
        <h2>Database Backups</h2>
        <button
          className="primary"
          onClick={() => trigger.mutate()}
          disabled={trigger.isPending}
        >
          {trigger.isPending ? "Starting…" : "Run Backup Now"}
        </button>
      </div>

      {trigger.isError && (
        <p className="error-text">Failed to start backup job.</p>
      )}

      {isLoading && <p className="muted">Loading…</p>}

      {data && data.jobs.length === 0 && (
        <div className="card">
          <p className="muted">No backup jobs yet. Click "Run Backup Now" to start one.</p>
        </div>
      )}

      {data && data.jobs.length > 0 && (
        <div className="card table-scroll" style={{ padding: 0 }}>
          <table>
            <thead>
              <tr>
                <th>Status</th>
                <th>Triggered by</th>
                <th>Started</th>
                <th>Duration</th>
                <th>Databases</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.jobs.map((job) => (
                <JobRow key={job.id} job={job} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            ← Prev
          </button>
          <span className="muted" style={{ lineHeight: "32px" }}>
            Page {page} of {totalPages}
          </span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next →
          </button>
        </div>
      )}
    </>
  );
}
